import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Lightbulb, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getInsightBySlug } from '@/data/staticInsights';

function RichInsightContent({ insight }) {
  return (
    <div className="space-y-14 md:space-y-16">
      <section aria-labelledby="executive-summary">
        <div className="border-l-4 border-teal bg-secondary px-6 py-6 md:px-8 md:py-7">
          <p id="executive-summary" className="text-xs uppercase tracking-[0.18em] font-bold text-teal mb-3">
            Executive Summary
          </p>
          <p className="text-base md:text-lg text-navy/75 leading-relaxed">
            {insight.executive_summary}
          </p>
        </div>
      </section>

      <section aria-labelledby="key-observations">
        <div className="flex items-center gap-3 mb-6">
          <Lightbulb className="h-5 w-5 text-accent-orange" />
          <h2 id="key-observations" className="font-heading text-2xl md:text-3xl font-medium text-navy">
            Key Observations
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insight.key_observations.map((observation) => (
            <div key={observation.title} className="border border-border p-5 md:p-6 rounded-sm">
              <h3 className="font-heading text-base md:text-lg font-medium text-navy mb-2">
                {observation.title}
              </h3>
              <p className="text-sm md:text-base text-navy/60 leading-relaxed">
                {observation.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {insight.sections.map((section) => (
        <section key={section.heading}>
          <h2 className="font-heading text-2xl md:text-3xl font-medium text-navy leading-tight mb-5">
            {section.heading}
          </h2>

          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph} className="text-base text-navy/65 leading-7 mb-5">
              {paragraph}
            </p>
          ))}

          {section.subsections && (
            <div className="divide-y divide-border border-y border-border mt-7">
              {section.subsections.map((subsection) => (
                <div key={subsection.title} className="py-6 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-8">
                  <h3 className="font-heading text-base font-medium text-navy">
                    {subsection.title}
                  </h3>
                  <p className="text-sm md:text-base text-navy/60 leading-relaxed">
                    {subsection.text}
                  </p>
                </div>
              ))}
            </div>
          )}

          {section.bullets && (
            <ul className="space-y-3 mt-6">
              {section.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-3 text-base text-navy/65 leading-relaxed">
                  <CheckCircle2 className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section aria-labelledby="success-metrics">
        <h2 id="success-metrics" className="font-heading text-2xl md:text-3xl font-medium text-navy mb-5">
          Metrics That Demonstrate Success
        </h2>
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-[190px_1fr] bg-navy text-white text-xs uppercase tracking-[0.14em] font-bold">
            <div className="px-5 py-4">Measurement Area</div>
            <div className="px-5 py-4 border-l border-white/10">Useful Indicators</div>
          </div>
          {insight.metrics.map((metric) => (
            <div key={metric.area} className="grid grid-cols-1 md:grid-cols-[190px_1fr] border-t first:border-t-0 md:first:border-t">
              <div className="px-5 pt-5 pb-2 md:py-5 bg-secondary font-heading text-sm font-medium text-navy">
                {metric.area}
              </div>
              <div className="px-5 pb-5 pt-1 md:py-5 text-sm text-navy/60 leading-relaxed md:border-l border-border">
                {metric.indicators}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="leadership-questions" className="bg-navy px-6 py-8 md:px-10 md:py-10">
        <h2 id="leadership-questions" className="font-heading text-2xl md:text-3xl font-medium text-white mb-6">
          Questions Leaders Should Ask
        </h2>
        <ol className="space-y-4">
          {insight.leadership_questions.map((question, index) => (
            <li key={question} className="flex items-start gap-4">
              <span className="flex items-center justify-center h-7 w-7 border border-teal text-teal text-xs font-bold shrink-0">
                {index + 1}
              </span>
              <span className="text-sm md:text-base text-white/75 leading-relaxed pt-0.5">
                {question}
              </span>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <h2 className="font-heading text-2xl md:text-3xl font-medium text-navy mb-5">Closing Perspective</h2>
        <p className="text-base text-navy/65 leading-7">{insight.conclusion}</p>
      </section>

      <section className="border-t border-border pt-10">
        <p className="text-xs uppercase tracking-[0.18em] font-bold text-accent-orange mb-3">{insight.cta.eyebrow}</p>
        <h2 className="font-heading text-2xl md:text-3xl font-medium text-navy mb-3">{insight.cta.title}</h2>
        <p className="text-base text-navy/60 leading-relaxed mb-6 max-w-2xl">{insight.cta.text}</p>
        <Link
          to={insight.cta.href}
          className="inline-flex items-center gap-2 bg-teal text-white px-5 py-3 text-sm font-medium hover:bg-teal/90 transition-colors"
        >
          {insight.cta.label}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
}

export default function InsightDetailPage() {
  const { slug } = useParams();
  const insight = getInsightBySlug(slug);

  if (!insight) {
    return (
      <div className="py-24 md:py-32 text-center">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h1 className="font-heading text-3xl text-navy mb-4">Insight Not Found</h1>
          <Link to="/insights" className="text-teal hover:text-teal/80 text-sm font-medium">
            <ArrowLeft className="inline h-4 w-4 mr-1" /> Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section data-testid="insight-detail" className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center text-sm text-teal hover:text-teal/80 font-medium mb-8">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Insights
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <Badge variant="secondary" className="bg-teal/10 text-teal border-0 text-xs rounded-full">
              {insight.category}
            </Badge>
            {insight.industry && (
              <span className="text-xs text-navy/40">{insight.industry}</span>
            )}
          </div>

          <h1 data-testid="insight-title" className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-navy leading-tight mb-6">
            {insight.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-navy/50 mb-8">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{insight.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>
                {new Date(insight.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                {insight.read_time && ` | ${insight.read_time}`}
              </span>
            </div>
          </div>

          {insight.image_url && (
            <div className="aspect-[16/9] rounded-sm overflow-hidden mb-10">
              <img src={insight.image_url} alt={insight.title} className="w-full h-full object-cover" />
            </div>
          )}

          <p className="text-lg md:text-xl text-navy/70 font-medium leading-relaxed mb-12">
            {insight.excerpt}
          </p>

          {insight.sections ? (
            <RichInsightContent insight={insight} />
          ) : (
            <div className="prose prose-lg max-w-none">
              {insight.content.split('\n\n').map((paragraph, i) => (
                <p key={i} className="text-base text-navy/60 leading-relaxed mb-5">{paragraph}</p>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
